import { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Grid,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
} from '@mui/material';
import { ThemePalette } from 'mui.theme';
import { StyledTableHeaderCell } from 'views/TirePressure/Components/NotificationTable.styles';
import { PNG } from 'components/Asset/PNG';
import { getImageNameByMakeModelAndAsset } from 'utils/helpers/mapAssetImage';
import { truncateTo2dp } from 'utils/helpers/general';
import type {
  utilizationFleetSummary,
  DeviceStats,
} from 'types/utilization.types';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CallMadeIcon from '@mui/icons-material/CallMade';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import { isNegative } from 'utils/fuelAnalytics';
import { convertsecondsToHours } from 'views/Utilization/utils';
import Chart from '../chart';
import { getUtilizationValuesFromData } from '../utils';
import type { DownTimeData } from 'store/SquaresStore/DashboardSquaresState.slice';
import RecommendationsList from '../RecommendationsList';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface Props {
  enableDarkTheme: boolean;
  customerCode: string;
  data: utilizationFleetSummary;
  chartData: DeviceStats[];
  unitSystem: string;
  downTimeData: DownTimeData;
  suggestions: any;
}

export const UtilizationWidget = ({
  enableDarkTheme,
  customerCode,
  data,
  chartData,
  unitSystem,
  downTimeData,
  suggestions,
}: Props) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const { current } = downTimeData;
  const formattedChartData = chartData.map((item) => {
    const itemDownTime =
      current.find((cdt) => cdt.asset_id === item.bumper_id)?.pm_duration ?? 0;
    return {
      ...item,
      down_time_hours: itemDownTime,
    };
  });
  const [showTableData, setShowTableData] = useState<boolean>(false);
  const [typeGrouping, setTypeGrouping] =
    useState<keyof typeof keyMappings>('Utilization');
  const fontColor = palette.textPrimary;
  const keyMappings = {
    Utilization: 'avg_utilization_by_whole_day',
    'Fuel Consumption': 'total_fuel_consumption_l',
    'Excess Idling': 'total_idling_duration',
    'Downtime Hours': 'down_time_hours',
  } as const;
  type KeyToDisplay = (typeof keyMappings)[keyof typeof keyMappings];
  const keyToDisplay: KeyToDisplay = useMemo(() => {
    return keyMappings[typeGrouping];
  }, [typeGrouping]);

  const handleChange = (event: any) => {
    setTypeGrouping(event.target.value);
  };

  const radioOptions = [
    'Utilization',
    'Fuel Consumption',
    'Excess Idling',
    'Downtime Hours',
  ];

  const getUtilizationValues = useCallback(
    (data: any) => {
      return getUtilizationValuesFromData(data, typeGrouping, downTimeData);
    },
    [typeGrouping]
  );

  const { utilizationValue, utilizationValueDiff } = getUtilizationValues(data);

  const utilizationUnit =
    typeGrouping === 'Fuel Consumption'
      ? unitSystem === 'metric'
        ? ' ltrs'
        : ' galns'
      : ['Excess Idling', 'Downtime Hours'].includes(typeGrouping)
      ? 'hrs'
      : '%';

  const diffIsNegative = isNegative(Number(utilizationValueDiff));

  return (
    <Box
      sx={{
        border: '1px solid #2F445E',
        borderRadius: '4px',
        height: showTableData ? '600px' : '220px',
        overflow: 'scroll',
        color: fontColor,
        marginTop: '20px',
        padding: '20px',
      }}
    >
      <Box
        sx={{
          height: '220px',
          color: palette.textPrimary,
        }}
      >
        <Grid container spacing={2}>
          <Grid
            item
            lg={3}
            sx={{
              height: '100%',
            }}
          >
            <Box
              sx={{
                padding: '10px',
                border: '1px solid #2F445E',
                height: '200px',
                borderRadius: '4px',
                backgroundColor: palette.paperBackground,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <FormControl>
                  <RadioGroup
                    row
                    aria-labelledby="demo-row-radio-buttons-group-label"
                    name="row-radio-buttons-group"
                    value={typeGrouping}
                    onChange={handleChange}
                    sx={{ fontSize: '14px' }}
                  >
                    {radioOptions.map((option) => (
                      <FormControlLabel
                        key={option}
                        value={option}
                        control={
                          <Radio
                            sx={{
                              fontSize: '12px',
                              color: enableDarkTheme
                                ? palette.primary
                                : ThemePalette.typography.primary,
                              '&.Mui-checked': {
                                color: enableDarkTheme
                                  ? palette.primary
                                  : ThemePalette.typography.primary,
                              },
                              '& .MuiSvgIcon-root': {
                                fontSize: 14,
                              },
                            }}
                          />
                        }
                        label={option}
                        sx={{
                          '& .MuiFormControlLabel-label': {
                            fontSize: '14px',
                            color: ThemePalette.typography.grey,
                          },
                        }}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              </Box>
              <Typography
                sx={{
                  fontSize: '35px',
                  fontWeight: '700',
                }}
              >
                {truncateTo2dp(utilizationValue)}
                <span
                  style={{
                    marginLeft: '3px',
                    fontSize: '14px',
                    color: ThemePalette.typography.grey,
                  }}
                >
                  {utilizationUnit}
                </span>
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '12px',
                  color: ThemePalette.typography.grey,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    color: diffIsNegative ? '#EB5757' : '#009161',
                    marginRight: '4px',
                  }}
                >
                  {diffIsNegative ? (
                    <CallReceivedIcon
                      sx={{
                        fontSize: '14px',
                        marginRight: '3px',
                        color: ThemePalette.typography.lightDanger,
                      }}
                    />
                  ) : (
                    <CallMadeIcon
                      sx={{
                        fontSize: '14px',
                        marginRight: '3px',
                        color: ThemePalette.typography.lightSuccess,
                      }}
                    />
                  )}
                  {truncateTo2dp(Math.abs(utilizationValueDiff))}%{' '}
                  {diffIsNegative ? 'less' : 'more'}
                </Box>
                {t('from_last_period')}
              </Box>

              <Button
                sx={{
                  textTransform: 'none',
                  border: '1px solid #2F445E',
                  borderRadius: '4px',
                  marginTop: '35px',
                  fontSize: '12px',
                  color: ThemePalette.typography.white,
                }}
                onClick={() => {
                  setShowTableData(!showTableData);
                }}
              >
                {showTableData ? 'Hide Tabular Data' : 'Show Tabular Data'}
                {showTableData ? (
                  <ExpandLessIcon
                    fontSize="small"
                    sx={{
                      marginLeft: '4px',
                    }}
                  />
                ) : (
                  <ExpandMoreIcon
                    fontSize="small"
                    sx={{
                      marginLeft: '4px',
                    }}
                  />
                )}
              </Button>
            </Box>
          </Grid>
          <Grid
            item
            lg={5}
            sx={{
              height: '100%',
            }}
          >
            <Box
              sx={{
                padding: '10px',
                border: '1px solid #2F445E',
                height: '200px',
                borderRadius: '4px',
                backgroundColor: palette.paperBackground,
              }}
            >
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: ThemePalette.typography.grey,
                }}
              >
                {t('by_assets')}
              </Typography>
              <Box
                sx={{
                  height: '180px',
                  maxHeight: '180px',
                }}
              >
                {formattedChartData.length === 0 ? (
                  <Typography
                    sx={{
                      color: fontColor,
                      fontSize: '12px',
                      marginTop: '10px',
                    }}
                  >
                    {' '}
                    {t('no_assets_data')}
                  </Typography>
                ) : (
                  <Chart
                    enableDarkTheme={enableDarkTheme}
                    data={formattedChartData}
                    chartKey={keyToDisplay}
                    keyDisplay={typeGrouping}
                  />
                )}
              </Box>
            </Box>
          </Grid>
          <Grid item lg={4} sx={{ height: '100%' }}>
            <Box
              sx={{
                padding: '10px',
                overflow: 'scroll',
                border: '1px solid #2F445E',
                height: '200px',
                borderRadius: '4px',
                backgroundColor: palette.paperBackground,
              }}
            >
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: ThemePalette.typography.grey,
                }}
              >
                {t('suggestions_actions')}
              </Typography>

              <RecommendationsList
                data={suggestions}
                enableDarkTheme={enableDarkTheme}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>
      {showTableData && (
        <Box
          sx={{
            border: '1px solid #2F445E',
            marginTop: '40px',
            padding: '10px',
            borderRadius: '4px',
            backgroundColor: palette.paperBackground,
          }}
        >
          {formattedChartData.length === 0 ? (
            <Typography
              sx={{
                color: fontColor,
                fontSize: '12px',
                marginTop: '10px',
              }}
            >
              {' '}
              {t('no_assets_data')}
            </Typography>
          ) : (
            <TableContainer
              sx={{
                border: '1px solid #2F445E',
                borderRadius: '4px',
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <StyledTableHeaderCell enabledarktheme={enableDarkTheme}>
                      {t('asset_details')}
                    </StyledTableHeaderCell>
                    <StyledTableHeaderCell enabledarktheme={enableDarkTheme}>
                      {t('utilization')}
                    </StyledTableHeaderCell>
                    <StyledTableHeaderCell enabledarktheme={enableDarkTheme}>
                      {t('fuel_consumption')}
                    </StyledTableHeaderCell>
                    <StyledTableHeaderCell enabledarktheme={enableDarkTheme}>
                      {t('idling_hours')}
                    </StyledTableHeaderCell>
                    <StyledTableHeaderCell enabledarktheme={enableDarkTheme}>
                      {t('downtime_hours')}
                    </StyledTableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formattedChartData.map((row: any, index: number) => (
                    <TableRow
                      key={index}
                      sx={{
                        '& td': {
                          border: 'none',
                          borderBottom:
                            index !== formattedChartData.length - 1
                              ? '1px solid #2F445E'
                              : 'none',
                        },
                      }}
                    >
                      <TableCell>
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
                        >
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              bgcolor: palette.toolBarBackground,
                              borderRadius: '83px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <PNG
                              name={getImageNameByMakeModelAndAsset(
                                row.make,
                                row.model,
                                row.asset_type,
                                'Small'
                              )}
                              alt={t('assetimage')}
                              style={{
                                height: '35px',
                                width: '100%',
                              }}
                            />
                          </Box>
                          <Box>
                            <Typography
                              sx={{
                                fontSize: '12px',
                                color: fontColor,
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'left',
                                }}
                              >
                                {row.bumper_id}
                              </Box>
                            </Typography>
                            <Typography
                              sx={{ fontSize: '12px', color: fontColor }}
                            >
                              {`${row.make}-${row.model}`}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography
                          sx={{
                            fontSize: '12px',
                            color: fontColor,
                          }}
                        >
                          {truncateTo2dp(row.avg_utilization_by_whole_day)}%
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          sx={{
                            fontSize: '12px',
                            color: fontColor,
                          }}
                        >
                          {truncateTo2dp(row.total_fuel_consumption_l)}
                          {unitSystem === 'metric' ? ' ltrs' : ' galns'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          sx={{
                            fontSize: '12px',
                            color: fontColor,
                          }}
                        >
                          {truncateTo2dp(
                            convertsecondsToHours(row.total_idling_duration)
                          )}
                          hrs
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          sx={{
                            fontSize: '12px',
                            color: fontColor,
                          }}
                        >
                          {truncateTo2dp(row.down_time_hours / (60 * 60 * 60))}
                          hrs
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}
    </Box>
  );
};
